import React, {FormEvent, ReactNode, useEffect, useState} from 'react';
import {useZooPassAppContext} from "../../Context/ZooPassAppContext";
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import {IZooPassAnswersContext, useZooPassAppAnswersContext, ZooPassAppAnswersContext} from "../../Context/ZooPassAnswersContext";
import {StepTitleContent} from "../PanelContents/StepTitleContent";
import ZooPassFetch from "../../Utilities/ZooPassFetch";
import {MembershipDto, ZooMemberDetails} from "../../Data/TnewMemberDetails";
import ZooPassStepDataUtility from "../../Utilities/ZooPassStepDataUtility";
import ZooPassLocalStateUtility from "../../Utilities/ZooPassLocalStateUtility";
import {RenewalAnswerCard} from "../PanelContents/RenewalAnswerCard";
import {
    GuestMemberLookupInputValues,
    RenewalGiftMemberLookupFormSegment,
    RenewalHrSegment,
    RenewalLoadingSegment,
    RenewalLoginSegment,
    RenewalStartANewPassSegment
} from "../PanelContents/RenewalSupportingSegments";

enum RenewalStateFlags {
    None = 0,
    TranslationLoaded = 1 << 0,
    DataLoaded = 1 << 1,
    ProfileLoaded = 1 << 2,
    All = ~(~0 << 3)
}

export const Step2GuestRenewalPanel: React.FC = () => {

    let {
        dictionary,
        textTitles,
        zooPassMembership,
        loading,
        memberShipDetails
    } = useZooPassAppContext();

    let {
        answersData,
        answersLoading,
        updateContext
    } = useZooPassAppAnswersContext();

    const [nonActionableRows, setNonActionableRows] = useState<ReactNode[]>([]);
    const [actionableRows, setActionableRows] = useState<ReactNode[]>([]);
    

    /////////////////////////////////////////////////
    // Handle the load state
    const [stateFlags, setStateFlags] = useState<number>(RenewalStateFlags.None);
    useEffect(() => {
        //Set the loading flags ---------------------------------------
        let newFlags: number = RenewalStateFlags.None;
        if (!loading) newFlags ^= RenewalStateFlags.TranslationLoaded;
        if (!answersLoading) newFlags ^= RenewalStateFlags.DataLoaded;
        if (memberShipDetails && !answersLoading && answersData?.step == 2.02) newFlags ^= RenewalStateFlags.ProfileLoaded;
        setStateFlags(newFlags);
    }, [loading, dictionary, answersLoading, memberShipDetails, answersData?.step]);
    const [showLoading, setShowLoading] = useState<boolean>(false);
    const [showLogin, setShowLogin] = useState<boolean>(false);
    const [showOptions, setShowOptions] = useState<boolean>(false);
    useEffect(() => {
        setShowLoading(stateFlags != RenewalStateFlags.All);
        setShowLogin(stateFlags == RenewalStateFlags.All && !(memberShipDetails?.isLoggedIn ?? false));
        setShowOptions(stateFlags == RenewalStateFlags.All && (memberShipDetails?.isLoggedIn ?? false));
    }, [stateFlags]);
    // END: Handle the load state
    /////////////////////////////////////////////////

    /////////////////////////////////////////////////
    // Handle search

    const[loadingSearch, setLoadingSearch] = useState<boolean>(false);
    const handleSearchClick = (values : GuestMemberLookupInputValues) => {
        setLoadingSearch(true);
        ZooPassFetch.searchRenewalMemberInfo(window.baseApiUrl, values.ln, values.membershipId)
        .then(lookupResponse => {
            console.log(lookupResponse);
            if(!lookupResponse.success)
            {
                console.warn("Search Failed");
                console.warn(lookupResponse.message);
                setActionableRows([]);
                setNonActionableRows([]);
                return;
            }
            const actionAr: ReactNode[] = [];
            const nonActionableAr: ReactNode[] = [];
            lookupResponse.member?.actionableMemberships.forEach((detail) => {
                actionAr.push(<RenewalAnswerCard detail={detail} onSelect={() => onItemSelected(detail)}
                                              key={detail.id}/>);
            });
            lookupResponse.member?.nonActionableMemberships.forEach((detail) => {
                nonActionableAr.push(<RenewalAnswerCard detail={detail} onSelect={() => onItemSelected(detail)}
                                                 key={detail.id}/>);
            });
            setActionableRows(actionAr);
            setNonActionableRows(nonActionableAr);
        }).catch(error => {
            console.warn("Search Error");
            console.warn(error);
            setActionableRows([]);
            setNonActionableRows([]);
        }).finally(() => {
            setLoadingSearch(false);
        })
    }
    
    // END: Handle search
    /////////////////////////////////////////////////

    const onItemSelected = (membership: MembershipDto) => {
        console.log("onItemSelected", membership);
        if (answersData == null) return;
        const offering = zooPassMembership.get(membership.membershipLevel.code);
        console.log("offering", offering)
        if (offering == undefined) {
            console.log("Offering for membership not found: ", membership);
            return;
        }
        const data = ZooPassStepDataUtility.SetStepDataFromRenewal(answersData, offering.lookup, offering.lookup.children ? 9 : 0);
        data.previousStep = answersData.step;
        data.step = 3;
        data.formData.memberID = membership.constituent.id;
        ZooPassLocalStateUtility.SetLocalState(data);
        updateContext({type: "setAnswers", value: data});
    }

    const handleNewItemClick = () => onNewSelectedClick();

    const onNewSelectedClick = () => {
        if (answersData != null && !answersLoading) {
            answersData.step2Value = 0;
            answersData.previousStep = answersData.step;
            answersData.step = 3;
            ZooPassLocalStateUtility.SetLocalState(answersData);
            updateContext({type: "setAnswers", value: answersData});
        }
    }

    const handleLoginClick = () => {
        console.log("Login Click");
        ZooPassFetch.redirectTNewLogin(window.baseApiUrl);
    };

    return !answersLoading && !loading &&
        textTitles !== null &&
        answersData !== null && (answersData.step == 2.02) && (
            <div className="container d-block step2-renewal-wrapper">
                {showLoading && <RenewalLoadingSegment/>}
                {showLogin && <RenewalLoginSegment clickCallback={handleLoginClick} />}
                {showOptions && <>
                    {loadingSearch && <>
                        <RenewalLoadingSegment/>
                    </>}
                    {!loadingSearch && <>
                        {/*Options should be displayed + no rows*/}
                        {(actionableRows.length == 0 && nonActionableRows.length == 0) && <>
                            <StepTitleContent textTitle={textTitles.get("ZooPass:PageContent:02:Renew:No")}/>
                            <div
                                className="row g-4 align-items-center justify-content-center row-cols-xl-3 row-cols-lg-2 row-cols-md-2 row-cols-1">
                                <div className="col justify-content-center align-content-center nothing-to-see zoo-pass__nothing-to-see">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                         className="nothing-to-see__alert-icon">
                                        <path
                                            d="M11 15h2v2h-2v-2m0-8h2v6h-2V7m1-5C6.47 2 2 6.5 2 12c0 5.52 4.48 10 10 10s10-4.48 10-10S17.52 2 12 2m0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"></path>
                                    </svg>
                                </div>
                            </div>
                        </>}
                        
                        {/*Options should be displayed + rows found*/}
                        {(actionableRows.length > 0 || nonActionableRows.length > 0) && <StepTitleContent textTitle={textTitles.get("ZooPass:PageContent:02:Renew:Yes")}/>}
                        {actionableRows.length > 0 && <>
                            <div
                                className="row g-4 align-items-center justify-content-center row-cols-xl-3 row-cols-lg-2 row-cols-md-1 row-cols-sm-1 row-cols-1">
                                <div className="col">
                                    {actionableRows}
                                </div>
                            </div>
                        </>}
                        {(actionableRows.length > 0 && nonActionableRows.length > 0) && <RenewalHrSegment/>}
                        {nonActionableRows.length > 0 && <>
                            <div
                                className="row g-4 align-items-center justify-content-center row-cols-xl-3 row-cols-lg-2 row-cols-md-1 row-cols-sm-1 row-cols-1">
                                <div className="col">
                                    {nonActionableRows}
                                </div>
                            </div>
                        </>}
                    </>}
                    <RenewalGiftMemberLookupFormSegment onSubmit={handleSearchClick}/>
                    <RenewalHrSegment/>
                    <RenewalStartANewPassSegment clickCallback={handleNewItemClick} />
                </>
                }
            </div>

        );
};

